import { useEffect, useState, createContext, useContext, useCallback, type ReactNode } from 'react'
import { X, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react'

type ToastType = 'success' | 'warning' | 'error' | 'info'

interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => void
  dismissToast: (id: string) => void
}

const ToastContext = createContext<ToastContextType | null>(null)

// Toast Provider
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    setToasts((prev) => [...prev, { ...toast, id }])
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

// Toast Container
function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: string) => void
}) {
  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 max-w-sm">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

// Individual Toast
const TOAST_CONFIG: Record<ToastType, { Icon: typeof CheckCircle; className: string }> = {
  success: { Icon: CheckCircle, className: 'toast-success' },
  warning: { Icon: AlertTriangle, className: 'toast-warning' },
  error: { Icon: XCircle, className: 'toast-error' },
  info: { Icon: Info, className: 'toast-info' },
}

const ICON_COLOR: Record<ToastType, string> = {
  success: 'text-green-600',
  warning: 'text-amber-600',
  error: 'text-red-600',
  info: 'text-blue-600',
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast
  onDismiss: (id: string) => void
}) {
  const { Icon, className } = TOAST_CONFIG[toast.type]
  const iconColor = ICON_COLOR[toast.type]
  const duration = toast.duration ?? 5000

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(toast.id)
      }, duration)
      return () => clearTimeout(timer)
    }
  }, [toast.id, duration, onDismiss])

  return (
    <div className={`toast ${className}`} role="alert">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-slate-900">{toast.title}</p>
          {toast.message && (
            <p className="text-sm text-slate-600 mt-0.5">{toast.message}</p>
          )}
        </div>
        <button
          onClick={() => onDismiss(toast.id)}
          className="flex-shrink-0 p-1 rounded hover:bg-stone-100 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>
    </div>
  )
}

// Standalone toast component for simple usage
interface StandaloneToastProps {
  type: ToastType
  title: string
  message?: string
  onDismiss?: () => void
}

export function NotificationToast({ type, title, message, onDismiss }: StandaloneToastProps) {
  const { Icon, className } = TOAST_CONFIG[type]
  const iconColor = ICON_COLOR[type]

  return (
    <div className={`toast ${className}`} role="alert">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-slate-900">{title}</p>
          {message && (
            <p className="text-sm text-slate-600 mt-0.5">{message}</p>
          )}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1 rounded hover:bg-stone-100 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>
    </div>
  )
}
